import React from 'react';
import { Tooltip, Table, Dropdown, DropdownSort, DropdownItem, Icon, Status, InfoText, TableCellDropdown, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import LoadingTable from '../../../components/loadingTable';
import css from './imports.module.css';
import useAppContext from '../../../context/useAppContext';

const HEADER = [
    {
        name: 'crst',
        label: 'date',
        class: 'small',
        items: [
            { key: 'ascending', it: 'crst' },
            { key: 'descending', it: '-crst' }
        ]
    },
    {
        name: 'alpha',
        label: 'file',
        class: 'large',
        items: [
            { key: 'A-Z', it: 'alpha' },
            { key: 'Z-A', it: '-alpha' }
        ]
    },
    {
        name: 'orig',
        label: 'origin',
        class: 'xsmall',
        items: [
            { key: 'A-Z', it: 'orig' },
            { key: 'Z-A', it: '-orig' }
        ]
    },
    {
        name: 'atts',
        label: 'new-attendees',
        class: 'medium',
        items: [
            { key: 'ascending', it: 'atts' },
            { key: 'descending', it: '-atts' }
        ]
    }
];

export const ImportsTable = ({ ids, event, imports, sync, handleSelect, handleSelectAll, selected, order, setOrder, loading }) => {
    const { t } = useTranslation();
    const [context] = useAppContext();
    React.useEffect(() => {
        if (context.apiSync) {
            context.apiSync = false;
            sync();
        }
    }, [context]);

    const sortedImports = React.useMemo(() => {
        if (!imports || !imports.length || loading) {
            return [];
        }

        switch (order) {
            case 'alpha':
                imports.sort((a, b) => a.source?.localeCompare(b?.source));
                break;
            case '-alpha':
                imports.sort((b, a) => a.source?.localeCompare(b?.source));
                break;
            case 'orig':
                imports.sort((a, b) => a.process?.localeCompare(b?.process));
                break;
            case '-orig':
                imports.sort((b, a) => a.process?.localeCompare(b?.process));
                break;
            case 'crst':
                imports.sort((a, b) => a.created?.localeCompare(b?.created));
                break;
            case '-crst':
                imports.sort((b, a) => a.created?.localeCompare(b?.created));
                break;
            case 'atts':
                imports.sort((a, b) => a.summary?.created - b?.summary?.created);
                break;
            case '-atts':
                imports.sort((b, a) => a.summary?.created - b?.summary?.created);
                break;
            default:
                break;
        }

        return imports;
    }, [imports, loading, order]);

    return (
        <>
            <Table useNew selectable onSelectAll={handleSelectAll} className={css['imports-table']}>
                <Table.THead>
                    <Table.Tr isHeader active={ids.every(id => selected.includes(id))}>
                        {HEADER.map((dropdown, index) => {
                            return (
                                <th key={index}>
                                    <DropdownSort
                                        className={css[`imports-table-${dropdown.class}`]}
                                        name={dropdown.name}
                                        active={[dropdown.name, `-${dropdown.name}`].includes(order)}
                                        label={t(`ticket.attendees.imports.table.${dropdown.label}`)}
                                        ordering={order}
                                        items={dropdown.items.map((item, i) => (
                                            <DropdownItem.Icons
                                                key={i}
                                                item={t(`ticket.common.ordering.${item.key}`)}
                                                checked={order === item.it}
                                                onClick={() => {
                                                    setOrder(item.it);
                                                }}
                                            />
                                        ))}
                                    />
                                </th>
                            );
                        })}
                        <th className={css['imports-table-medium']}>{t(`ticket.attendees.imports.table.status`)}</th>
                        <th className={css['imports-table-medium']}>{t(`ticket.attendees.imports.table.action`)}</th>
                    </Table.Tr>
                </Table.THead>
                <Table.TBody>
                    {sortedImports?.length > 0 &&
                        sortedImports?.map(imp => {
                            return <ImportItem key={imp.id} imp={imp} event={event} handleSelect={handleSelect} selected={selected} />;
                        })}
                </Table.TBody>
            </Table>
            <LoadingTable loading={loading} height="40vh" />
        </>
    );
};

const ImportItem = ({ imp, event, selected, handleSelect }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const statusLabel = React.useMemo(() => {
        const {
            status,
            summary: { error, invalid }
        } = imp;
        switch (status) {
            case 'in-progress':
                return status;
            case 'done':
                return error || invalid ? 'incomplete' : 'imported';
            case 'not-valid':
            case 'valid':
                return 'pending';
            default:
                break;
        }
    }, [imp]);

    const status = React.useMemo(() => {
        switch (statusLabel) {
            case 'pending':
                return { color: 'gray', label: t('ticket.attendees.imports.status.pending') };
            case 'in-progress':
                return { color: 'orange', label: t('ticket.attendees.imports.status.in-progress') };
            case 'imported':
                return { color: 'green', label: t('ticket.attendees.imports.status.imported') };
            case 'incomplete':
                return { color: 'red', label: t('ticket.attendees.imports.status.incomplete') };
            default:
                break;
        }
    }, [imp, statusLabel]);

    const renderStatus = React.useCallback(
        status => {
            const statusComponent = <Status color={status.color} responsive text={status.label} />;
            return statusLabel === 'incomplete' || statusLabel === 'pending' ? (
                <Tooltip title={t(`ticket.attendees.imports.status.${statusLabel}_tooltip`)} triggerOnHover={true} margin={false}>
                    {statusComponent}
                </Tooltip>
            ) : (
                statusComponent
            );
        },
        [statusLabel, status]
    );

    const canDownloadErrorReport = React.useMemo(() => {
        const { report } = imp;
        return report && (statusLabel === 'incomplete' || statusLabel === 'imported');
    }, [statusLabel, imp]);

    const downloadErrorReport = React.useCallback(() => {
        const { report } = imp;
        if (report) {
            fetch(report)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    window.open(report, '_blank');
                })
                .catch(error => toast.error(t('ticket.attendees.imports.table.actions.error-download-import-report')));
        }
    }, [imp]);

    const numberAttendeeDone = React.useMemo(() => {
        if (imp.status === 'done') {
            return imp.summary.created;
        }
        return 0;
    }, [imp]);

    const numberAttendeeTotal = React.useMemo(() => {
        return imp.summary.created + imp.summary.error + imp.summary.invalid;
    }, [imp]);

    const importLabel = React.useMemo(() => {
        if (numberAttendeeDone === 0 && numberAttendeeTotal === 0) {
            return t('ticket.attendees.imports.table.processing');
        }
        return `${numberAttendeeDone} / ${numberAttendeeTotal}`;
    }, [imp, numberAttendeeDone, numberAttendeeTotal]);

    const fileName = React.useMemo(() => {
        // ID (PendingUpload) of the key is different from ID of the import request
        // we replace it by the import ID
        const [, rest] = imp.source.split('/', 2);
        return [imp.id, rest].join('/');
    }, [imp]);

    return (
        <Table.Tr onSelected={() => handleSelect(imp.id)} active={selected.includes(imp.id)}>
            <Table.Td>
                <InfoText subtitle={new Intl.DateTimeFormat(event.default_language, { timeStyle: 'short' }).format(moment(imp.created).tz(event.timezone, true))}>
                    {new Intl.DateTimeFormat(event.default_language, { dateStyle: 'short' }).format(moment(imp.created).tz(event.timezone, true))}
                </InfoText>
            </Table.Td>
            <Table.Td>{fileName}</Table.Td>
            <Table.Td>{imp.process}</Table.Td>
            <Table.Td>
                <Icon name="users" />
                {importLabel}
            </Table.Td>
            <Table.Td>{renderStatus(status)}</Table.Td>
            <TableCellDropdown>
                <Dropdown.Item disabled={!canDownloadErrorReport} item={t('ticket.attendees.imports.table.actions.download-import-report')} onClick={downloadErrorReport} />
            </TableCellDropdown>
        </Table.Tr>
    );
};
